import React from 'react';
import { Button, Tag, Tooltip } from 'antd';
import { RightOutlined } from '@ant-design/icons';
import { Link } from 'react-admin';
import { useHistory } from 'react-router-dom';
import { getTagForAIProcessResponse } from '@src/components/Common/Tags';
import { CustomPriceTag } from '@src/components/Common/utils';
import { formatDateAndTime, formatDateAndTimeFromNow } from '@src/helpers/utils';

/**
 * One pipeline under the shared column header: what it ran against on the left,
 * then its state, stage and cost, with its actions closing the row.
 *
 * The identity is one line — number, what it ran against, which version, and
 * when — so a row is as tall as its type and no taller.
 *
 * The row opens the pipeline. The scope name goes somewhere else — the service
 * item it ran against — so it stops the click from reaching the row.
 */
const PipelineRow = ({
    record,
    onRetrigger,
}: {
    record: any;
    /** omitted where the viewer cannot trigger a run */
    onRetrigger?: (serviceItemId: number) => void;
}) => {
    const history = useHistory();
    const detail = `/owner_service_items/${record.scope_id}?tab=netorca_pack&pipeline_version=${record.version}`;
    const open = () => history.push(detail);

    return (
        <div
            className="pl-row"
            role="button"
            tabIndex={0}
            aria-label={`Open pipeline ${record.id}`}
            onClick={open}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    open();
                }
            }}
        >
            <div className="pl-identity">
                <span className="pl-identity__id">#{record.id}</span>
                <Tooltip placement="topLeft" title={record.scope_name}>
                    <Link
                        className="pl-identity__name"
                        to={`/owner_service_items/${record.scope_id}`}
                        onClick={(e: React.MouseEvent) => e.stopPropagation()}
                    >
                        {record.scope_name || '—'}
                    </Link>
                </Tooltip>
                <span className="pl-version">v{record.version}</span>
                {record.applied && <Tag className="pl-applied" color="#87d068">Applied</Tag>}
                {record.created && (
                    <Tooltip title={`Created ${formatDateAndTime(record.created)}`}>
                        <span className="pl-identity__meta">{formatDateAndTimeFromNow(record.created)}</span>
                    </Tooltip>
                )}
            </div>

            <div className="pl-state">{getTagForAIProcessResponse(record.state)}</div>

            <div className="pl-cell" data-label="Stage">{record.current_stage || '—'}</div>

            <div className="pl-cell pl-cell--cost" data-label="Cost">{CustomPriceTag(record.cost)}</div>

            <div className="pl-actions">
                {onRetrigger && (
                    <Button
                        size="small"
                        type="primary"
                        onClick={(e) => { e.stopPropagation(); onRetrigger(record.scope_id); }}
                    >
                        Retrigger
                    </Button>
                )}
                <span className="pl-chevron" aria-hidden="true"><RightOutlined /></span>
            </div>
        </div>
    );
};

export default PipelineRow;
